import type { PrepaidHoursLike } from '../../components/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BudgetBurnLike } from '../../components/ui';
import { api } from '../../lib/api';

// ---------------------------------------------------------------------------
// Wire types (mirror backend/src/modules/agreements/agreements.service.ts)
// ---------------------------------------------------------------------------

export type AgreementType = 'PROJECT' | 'SUPPORT' | 'RECURRING' | 'INTERNAL';
export type BillBy = 'HOURS' | 'FIXED' | 'NONE';

/**
 * How the agreement earns — decides what every other figure on the row means.
 * Set by the server (see backend/src/modules/agreements/agreement-metrics.service.ts);
 * the UI never infers it from type/billBy.
 */
export type MetricsBasis = 'HOURLY' | 'MONTHLY_FEE' | 'FIXED_FEE' | 'NON_BILLABLE';

export type BurnInfo = BudgetBurnLike;

export interface AgreementMetrics {
  basis: MetricsBasis;
  firstMonth: string | null;
  lastMonth: string | null;
  hours: number;
  billableHours: number;
  /**
   * Hourly-rate valuation of billable time. **Revenue ONLY when basis =
   * HOURLY.** On a fixed-bid or fee agreement the client agreed a price, so this
   * figure is money nobody will ever be charged — never print it as revenue,
   * never call it "billable" there, and never sit it beside a margin struck on
   * another basis. That pairing is the "big billable, deeply negative margin"
   * contradiction the canonical definitions outlaw.
   */
  billableAmount: number;
  cost: number;
  expenses: number;
  /** Fee value: the fixed fee, or monthly fee × months live. */
  contracted: number | null;
  feeMonths: number | null;
  invoiced: number;
  /** The one revenue figure margin is computed on. */
  revenue: number;
  /** Where `revenue` came from — never inferred from `basis` by a screen.
   *  `CONTRACT` = a fixed-bid agreement's agreed price (the PROJECT family). */
  revenueSource: 'BILLABLE_VALUE' | 'CONTRACT' | 'INVOICED' | 'NONE';
  margin: number | null;
  burn: BurnInfo | null;
  teamCount: number;
  /** null when the agreement is not billed hourly — "unbilled" would be a lie. */
  unbilledAmount: number | null;
  /** Uninvoiced billable expenses on the agreement — owed on every basis. */
  unbilledExpenses: number;
  /**
   * ⚠ THE figure to print under the word "Unbilled": the time half (hourly
   * only, net of the prepaid bank) PLUS the expenses. It is what the invoice
   * wizard will actually offer, so a screen printing `unbilledAmount` instead
   * reports less than the button beside it bills.
   */
  unbilledTotal: number;
}

export interface ClientLite {
  id: string;
  name: string;
  isActive?: boolean;
  /** Present on `/agreements/clients` — how many of its agreements are active. */
  activeAgreements?: number;
}

export interface AgreementCore {
  id: string;
  name: string;
  code: string | null;
  type: AgreementType;
  billBy: BillBy;
  isActive: boolean;
  client: ClientLite;
  budgetHours: number | null;
  budgetAmount: number | null;
  hourlyRate: number | null;
  startDate: string | null;
  endDate: string | null;
  /** Hub project this is linked to (Wave C2), or null. Our own copy of the
   *  link key — reading it costs no call to the hub. */
  /** Every hub project this agreement is linked to (many-to-many). */
  hubProjectIds: string[];
}

export interface AgreementRow extends AgreementCore {
  metrics: AgreementMetrics;
  /** Prepaid hours this agreement owns — null when it owns none. */
  prepaid: PrepaidHoursLike | null;
}

export interface PersonLite {
  id: string;
  name: string;
  email: string;
  type: 'STAFF' | 'CONTRACTOR';
  costModel: 'HOURLY' | 'FLAT_MONTHLY';
}

export interface PersonBreakdown {
  person: PersonLite;
  hours: number;
  billableHours: number;
  billableAmount: number;
  cost: number;
}

export interface MonthBreakdown {
  month: string; // YYYY-MM
  hours: number;
  billableAmount: number;
  /** Time cost + agreement expenses booked that month. */
  cost: number;
  expenses: number;
  /** Recognised revenue by invoice issue date — what margin is computed on. */
  invoiced: number;
  cumulativeHours: number;
  cumulativeBillable: number;
  cumulativeCost: number;
  cumulativeInvoiced: number;
}

export interface Assignment {
  id: string;
  person: PersonLite;
  billRate: number | null;
  costRate: number | null;
  resolvedBillRate: number;
  resolvedCostRate: number | null;
  hours: number;
  billableAmount: number;
  cost: number;
}

export interface AvailablePerson extends PersonLite {
  defaultBillRate: number | null;
}

export interface OrgTask {
  id: string;
  name: string;
  defaultBillable: boolean;
  defaultRate: number | null;
  isActive: boolean;
  /** Part of the firm's process: attached to every new client agreement. */
  isStandard: boolean;
  /** Process position; null sorts after the ordered tasks, by name. */
  sortOrder: number | null;
  agreementCount: number;
  activeAgreementCount: number;
  entryCount: number;
  hours: number;
  hoursLastYear: number;
  /** YYYY-MM-DD of the latest entry, or null. */
  lastUsed: string | null;
}

export interface OrgTaskList {
  tasks: OrgTask[];
  archived: boolean;
  archivedCount: number;
  /** Active client agreements, and how many lack a standard task (null on the archive). */
  standardCoverage: { agreements: number; missing: number } | null;
  /** May this viewer change the task list (owners/admins)? */
  canManage: boolean;
}

export interface TaskCore {
  id: string;
  name: string;
  isStandard?: boolean;
  defaultBillable: boolean;
  defaultRate: number | null;
}

export interface AgreementTaskRow {
  id: string;
  task: {
    id: string;
    name: string;
    isActive: boolean;
    isStandard: boolean;
    defaultBillable: boolean;
    defaultRate: number | null;
  };
  /** Override; null = follow the task default. */
  billable: boolean | null;
  /** Override; null = task default → agreement rate. */
  rate: number | null;
  resolvedBillable: boolean;
  /** Rate configured for NEW time on this task. */
  resolvedRate: number | null;
  /** Where `resolvedRate` came from. */
  rateSource: 'OVERRIDE' | 'TASK_DEFAULT' | 'PROJECT' | 'NONE';
  /** Rate the tracked time ACTUALLY billed at (value ÷ billable hours). */
  effectiveRate: number | null;
  hours: number;
  billableHours: number;
  billableAmount: number;
  entryCount: number;
}

export interface TaskBreakdown {
  taskId: string | null;
  name: string;
  hours: number;
  billableHours: number;
  billableAmount: number;
  /** Delivery cost of this task's time. Excludes agreement expenses (untaskable). */
  cost: number;
  /** (billable value − cost) ÷ billable value. null when the task earned nothing. */
  margin: number | null;
  entryCount: number;
}

export interface TimeLedgerEntry {
  id: string;
  date: string;
  person: { id: string; name: string };
  task: { id: string; name: string } | null;
  hours: number;
  billable: boolean;
  /** Rate this entry actually valued at. */
  rate: number | null;
  amount: number;
  cost: number;
  notes: string | null;
  invoiced: boolean;
  deletedInSource: boolean;
  /**
   * Why a bulk correction cannot change this entry's task / note — the server's
   * own sentence, null when it can. Only rows on the requested page carry it.
   */
  locks: { task: string | null; notes: string | null } | null;
}

export type CorrectionField = 'task' | 'notes';
export type NotesMode = 'append' | 'replace' | 'fill';

export interface CorrectionUndoItem {
  id: string;
  value: string | null;
  expect: string | null;
}

export interface CorrectionResult {
  field: CorrectionField;
  changed: number;
  changedHours: number;
  unchanged: number;
  skipped: Array<{
    id: string;
    code: string;
    reason: string;
    date: string;
    person: string;
    hours: number;
  }>;
  /** Send back as `undo` (same agreement and field) to reverse this correction. */
  undo: CorrectionUndoItem[];
}

export type CorrectionInput = { agreementId: string; field: CorrectionField } & (
  | { ids: string[]; taskId: string | null }
  | { ids: string[]; mode: NotesMode; text: string }
  | { undo: CorrectionUndoItem[] }
);

export interface TimeLedgerFilters {
  from?: string;
  to?: string;
  personId?: string;
  taskId?: string;
  billable?: '' | 'true' | 'false';
  invoiced?: '' | 'true' | 'false';
  page?: number;
  pageSize?: number;
}

export interface TimeLedger {
  entries: TimeLedgerEntry[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  /** Totals across the WHOLE filtered set, not just this page. */
  totals: { hours: number; billableHours: number; amount: number; cost: number };
  facets: {
    people: Array<{ id: string; name: string; entries: number }>;
    tasks: Array<{ id: string; name: string; entries: number }>;
  };
}

export interface AgreementExpense {
  id: string;
  date: string;
  vendor: string | null;
  amount: number;
  billable: boolean;
  notes: string | null;
  account: { id: string; name: string; code: string | null };
}

/** The never-invoiced billable work on this agreement, and the range it spans. */
export interface UnbilledWork {
  /** Same figure as metrics.unbilledTotal — the hours AND the billable expenses. */
  amount: number;
  hours: number;
  entryCount: number;
  /** The expense half of `amount`, so the CTA can say what it is offering. */
  expenseCount: number;
  expenseAmount: number;
  /** Work dates of the oldest/newest unbilled item ('YYYY-MM-DD'), null when none. */
  from: string | null;
  to: string | null;
}

export interface AgreementDetail {
  agreement: AgreementCore;
  metrics: AgreementMetrics;
  unbilled: UnbilledWork;
  byPerson: PersonBreakdown[];
  byMonth: MonthBreakdown[];
  byTask: TaskBreakdown[];
  tasks: AgreementTaskRow[];
  availableTasks: TaskCore[];
  /** Standard tasks this (client) agreement does not offer yet. */
  missingStandardTasks: Array<{ id: string; name: string }>;
  assignments: Assignment[];
  availablePeople: AvailablePerson[];
  expenses: AgreementExpense[];
}

export interface AgreementInput {
  name?: string;
  clientId?: string;
  code?: string | null;
  type?: AgreementType;
  billBy?: BillBy;
  budgetHours?: number | null;
  budgetAmount?: number | null;
  hourlyRate?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  /** Create only: link to this hub project atomically (required by org policy). */
  hubProjectId?: string;
}

export interface AgreementListFilters {
  type?: AgreementType | '';
  clientId?: string;
  showArchived?: boolean;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useAgreements(filters: AgreementListFilters) {
  return useQuery({
    queryKey: ['agreements', filters.type ?? '', filters.clientId ?? '', !!filters.showArchived],
    queryFn: () =>
      api.get<{ agreements: AgreementRow[] }>('/agreements', {
        params: {
          type: filters.type || undefined,
          clientId: filters.clientId || undefined,
          active: filters.showArchived ? undefined : 'true',
        },
      }),
  });
}

export function useAgreementDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['agreements', 'detail', id],
    queryFn: () => api.get<AgreementDetail>(`/agreements/${id}`),
    enabled: !!id,
  });
}

function timeParams(filters: TimeLedgerFilters) {
  return {
    from: filters.from || undefined,
    to: filters.to || undefined,
    personId: filters.personId || undefined,
    taskId: filters.taskId || undefined,
    billable: filters.billable || undefined,
    invoiced: filters.invoiced || undefined,
    page: filters.page ?? 1,
    pageSize: filters.pageSize ?? 50,
  };
}

export function useAgreementTime(id: string | undefined, filters: TimeLedgerFilters, enabled = true) {
  return useQuery({
    queryKey: ['agreements', 'time', id, filters],
    queryFn: () => api.get<TimeLedger>(`/agreements/${id}/time`, { params: timeParams(filters) }),
    enabled: !!id && enabled,
    placeholderData: keepPreviousData,
  });
}

/** One-shot fetch of the whole filtered set — used to build the CSV export. */
export function fetchAgreementTimeAll(id: string, filters: TimeLedgerFilters) {
  return api.get<TimeLedger>(`/agreements/${id}/time`, {
    params: { ...timeParams(filters), page: 1, pageSize: 5000 },
  });
}

export function useAgreementClients() {
  return useQuery({
    queryKey: ['agreements', 'clients'],
    queryFn: () => api.get<{ clients: ClientLite[] }>('/agreements/clients'),
    staleTime: 60_000,
  });
}

/** The active tasks, in process order. */
export function useOrgTasks(enabled = true) {
  return useQuery({
    queryKey: ['agreements', 'tasks'],
    queryFn: () => api.get<OrgTaskList>('/agreements/tasks'),
    enabled,
  });
}

/** The archive, searched — it can hold thousands of old Harvest tasks. */
export function useArchivedTasks(search: string, enabled = true) {
  return useQuery({
    queryKey: ['agreements', 'tasks', 'archived', search],
    queryFn: () =>
      api.get<OrgTaskList>('/agreements/tasks', { params: { archived: '1', q: search || undefined, limit: 50 } }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

function useInvalidateAgreements() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['agreements'] });
}

/** May this viewer bulk-correct entries? The server's answer, never a role read here. */
export function useCorrectionAccess() {
  return useQuery({
    queryKey: ['time', 'corrections', 'access'],
    queryFn: () => api.get<{ canCorrect: boolean }>('/time/entries/corrections'),
    staleTime: 5 * 60_000,
  });
}

/**
 * Move selected entries to a task, or rewrite their notes — one request, one
 * transaction; every entry left alone comes back named with its lock.
 */
export function useCorrectEntries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CorrectionInput) => api.post<CorrectionResult>('/time/entries/correct', input),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['agreements'] });
      void qc.invalidateQueries({ queryKey: ['time'] });
    },
  });
}

/**
 * Bulk archive/restore — one request, one transaction, all-or-nothing (the
 * server refuses the whole batch if any id is foreign or source-locked, so
 * the confirm dialog's count is exactly what happens).
 */
export function useArchiveAgreements() {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: (input: { ids: string[]; archived: boolean }) =>
      api.post<{ count: number; archived: boolean }>('/agreements/archive', input),
    onSettled: invalidate,
  });
}

export function useCreateAgreement() {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: (input: AgreementInput) => api.post<{ agreement: AgreementCore }>('/agreements', input),
    onSuccess: invalidate,
  });
}

export function useUpdateAgreement(id: string) {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: (input: AgreementInput) => api.patch<{ agreement: AgreementCore }>(`/agreements/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useArchiveAgreement() {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api.post<{ agreement: { id: string; isActive: boolean } }>(
        `/agreements/${id}/${archived ? 'archive' : 'restore'}`,
      ),
    onSuccess: invalidate,
  });
}

export function useAddAssignment(agreementId: string) {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: (input: { personId: string; billRate?: number | null; costRate?: number | null }) =>
      api.post(`/agreements/${agreementId}/assignments`, input),
    onSuccess: invalidate,
  });
}

export function useUpdateAssignment(agreementId: string) {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: ({
      assignmentId,
      ...input
    }: {
      assignmentId: string;
      billRate?: number | null;
      costRate?: number | null;
    }) => api.patch(`/agreements/${agreementId}/assignments/${assignmentId}`, input),
    onSuccess: invalidate,
  });
}

export function useRemoveAssignment(agreementId: string) {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      api.delete(`/agreements/${agreementId}/assignments/${assignmentId}`),
    onSuccess: invalidate,
  });
}

// --- org tasks --------------------------------------------------------------

export interface TaskInput {
  name?: string;
  defaultBillable?: boolean;
  defaultRate?: number | null;
  isActive?: boolean;
  isStandard?: boolean;
}

/** Put the standard tasks in process order (every standard id, first to last). */
export function useOrderTasks() {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: (ids: string[]) => api.post<OrgTaskList>('/agreements/tasks/order', { ids }),
    onSuccess: invalidate,
  });
}

/** Archive or restore several tasks; history is untouched either way. */
export function useArchiveTasks() {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: (input: { ids: string[]; archived: boolean }) =>
      api.post<{ count: number; archived: boolean }>('/agreements/tasks/archive', input),
    onSuccess: invalidate,
  });
}

/** Attach the standard tasks to one agreement, or (no id) every active client agreement. */
export function useAttachStandardTasks(agreementId?: string) {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: () =>
      api.post<{ added: number; agreements: number }>(
        agreementId ? `/agreements/${agreementId}/tasks/standard` : '/agreements/tasks/attach-standard',
        {},
      ),
    onSuccess: invalidate,
  });
}

export function useCreateTask() {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: (input: TaskInput) => api.post<{ task: TaskCore }>('/agreements/tasks', input),
    onSuccess: invalidate,
  });
}

export function useUpdateTask() {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: ({ taskId, ...input }: TaskInput & { taskId: string }) =>
      api.patch<{ task: TaskCore }>(`/agreements/tasks/${taskId}`, input),
    onSuccess: invalidate,
  });
}

/** Hard delete — only succeeds while the task is on no agreement and has no time. */
export function useDeleteTask() {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: (taskId: string) => api.delete(`/agreements/tasks/${taskId}`),
    onSuccess: invalidate,
  });
}

// --- agreement ⇄ task attachment ----------------------------------------------

export function useAttachTask(agreementId: string) {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: (input: { taskId: string; billable?: boolean | null; rate?: number | null }) =>
      api.post(`/agreements/${agreementId}/tasks`, input),
    onSuccess: invalidate,
  });
}

export function useUpdateAgreementTask(agreementId: string) {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: ({
      agreementTaskId,
      ...input
    }: {
      agreementTaskId: string;
      billable?: boolean | null;
      rate?: number | null;
    }) => api.patch(`/agreements/${agreementId}/tasks/${agreementTaskId}`, input),
    onSuccess: invalidate,
  });
}

export function useDetachTask(agreementId: string) {
  const invalidate = useInvalidateAgreements();
  return useMutation({
    mutationFn: (agreementTaskId: string) =>
      api.delete(`/agreements/${agreementId}/tasks/${agreementTaskId}`),
    onSuccess: invalidate,
  });
}
